import { supabase } from "@/integrations/supabase/client";
import { generateCareers, type Career } from "@/data/careers";
import {
  calculateTraits,
  calculateRawTraitsPublic,
  extractConstraints,
  type TraitScores,
} from "@/lib/scoring";
import { scoreCareer, filterCareers } from "@/lib/careerFilter";
import { slugify } from "@/lib/careerSlug";
import { categoryLabel } from "@/lib/categoryLabels";

export interface TraitHighlight {
  label: string;
  value: number;
}

/**
 * Data prepared for the (paywalled) results-ready email.
 *
 * We deliberately do NOT expose career names or fit scores here - the top 5
 * matches are gated behind the £5.99 paywall on /results, and the email must
 * not undermine that. Instead we send:
 *   - the hero image for the #1 career (visual, no name)
 *   - a category-level hint (e.g. "Technology", "Skilled Trades")
 *   - the user's own trait highlights (proof of personalisation)
 *   - a plain count of locked matches
 */
export interface ResultsReadyEmailData {
  topCareerImageUrl?: string;
  topCategoryLabel?: string;
  traitHighlights: TraitHighlight[];
  lockedCount: number;
  totalCount: number;
}

const TRAIT_LABELS: Record<keyof TraitScores, string> = {
  people: "People-focused",
  creativity: "Creative",
  income: "Income-driven",
  freedom: "Independent",
  structure: "Structured",
  risk: "Risk-tolerant",
};

/**
 * Computes teaser data for the results-ready email from stored answers.
 * Returns null when no answers are stored.
 */
export async function buildTopCareersForResultsEmail(): Promise<ResultsReadyEmailData | null> {
  let answers: Record<number, number[]> | null = null;
  try {
    const raw = localStorage.getItem("careerGPS_answers");
    if (raw) answers = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!answers || Object.keys(answers).length === 0) return null;

  let top: Career | null = null;
  let traits: TraitScores | null = null;
  try {
    traits = calculateTraits(answers);
    const rawTraits = calculateRawTraitsPublic(answers);
    const constraints = extractConstraints(answers);
    const all = generateCareers(traits);
    const { kept } = filterCareers(all, constraints);

    const scored = kept.map((c) => ({
      ...c,
      matchPercentage: scoreCareer(c, rawTraits, constraints),
    }));

    const best = new Map<string, typeof scored[number]>();
    for (const c of scored) {
      const key = (c.name || "").trim().toLowerCase();
      if (!key) continue;
      const existing = best.get(key);
      if (!existing || c.matchPercentage > existing.matchPercentage) best.set(key, c);
    }
    const sorted = Array.from(best.values()).sort(
      (a, b) => b.matchPercentage - a.matchPercentage,
    );
    top = sorted[0] ?? null;
  } catch {
    return null;
  }

  if (!top || !traits) return null;

  // Pull the hero image for the top career so the email visually leads with
  // the #1 match without naming it.
  let topImageUrl: string | undefined;
  try {
    const slug = slugify(top.name);
    const { data } = await supabase
      .from("career_seo")
      .select("featured_image_url")
      .eq("career_slug", slug)
      .maybeSingle();
    const url = (data as { featured_image_url: string | null } | null)?.featured_image_url;
    if (url) topImageUrl = url;
  } catch {
    /* fallback handled in template */
  }

  // Top 3 trait scores as proof-of-personalisation.
  const traitEntries = (Object.keys(TRAIT_LABELS) as Array<keyof TraitScores>)
    .map((k) => ({ label: TRAIT_LABELS[k], value: Math.round((traits as TraitScores)[k]) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 3);

  return {
    topCareerImageUrl: topImageUrl,
    topCategoryLabel: categoryLabel(top.category),
    traitHighlights: traitEntries,
    lockedCount: 5,
    totalCount: 10,
  };
}

/**
 * Admin-only: top 5 matches with names + fit scores for the internal
 * assessment_completed notification. These are the locked matches the
 * paying visitor unlocks - NEVER send this to end users.
 */
export interface AdminTopMatch {
  title: string;
  fit: number;
}
export function buildTopMatchesForAdmin(): AdminTopMatch[] {
  return buildRankedForAdmin(0, 5);
}

/**
 * Admin-only: the free-preview careers the user actually sees on /results
 * (ranked 6-10). Lets the admin email mirror what the visitor saw.
 */
export function buildFreePreviewMatchesForAdmin(): AdminTopMatch[] {
  return buildRankedForAdmin(5, 5);
}

function buildRankedForAdmin(offset: number, take: number): AdminTopMatch[] {
  let answers: Record<number, number[]> | null = null;
  try {
    const raw = localStorage.getItem("careerGPS_answers");
    if (raw) answers = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!answers || Object.keys(answers).length === 0) return [];

  try {
    const traits = calculateTraits(answers);
    const rawTraits = calculateRawTraitsPublic(answers);
    const constraints = extractConstraints(answers);
    const all = generateCareers(traits);
    const { kept } = filterCareers(all, constraints);
    const scored = kept.map((c) => ({
      ...c,
      matchPercentage: scoreCareer(c, rawTraits, constraints),
    }));
    const best = new Map<string, typeof scored[number]>();
    for (const c of scored) {
      const key = (c.name || "").trim().toLowerCase();
      if (!key) continue;
      const existing = best.get(key);
      if (!existing || c.matchPercentage > existing.matchPercentage) best.set(key, c);
    }
    return Array.from(best.values())
      .sort((a, b) => b.matchPercentage - a.matchPercentage)
      .slice(offset, offset + take)
      .map((c) => ({ title: c.name, fit: Math.round(c.matchPercentage) }));
  } catch {
    return [];
  }
}

/**
 * Small helper for admin emails: derives a coarse "location" from the
 * browser (timezone + language). Not IP-based; just enough for the admin
 * to know roughly where a signup came from without extra network calls.
 */
export function detectBrowserLocation(): { timezone?: string; language?: string; referrer?: string } {
  if (typeof window === "undefined") return {};
  try {
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      referrer: document.referrer || undefined,
    };
  } catch {
    return {};
  }
}


